import * as React from 'react';
import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import toast from 'react-hot-toast';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/bootstrap.css';
import { PostRequestPayload } from 'constants/payloads/tournamentsRegister';
import { EventTypesEnum, useUpdatePlayerPhoneMutation } from 'types/generated/client';
import { EventCoupon, OrderTotal } from 'utils/shared/money/calculateEventOrderTotal';
import { stripDialCode } from 'utils/shared/phone/stripDialCode';
import { toDigits } from 'utils/shared/phone/toDigits';
import { toFormattedPhone } from 'utils/shared/phone/toFormatedPhone';
import { toSafePhone } from 'utils/shared/phone/toSafePhone';
import { useApiGateway } from 'hooks/useApi';
import { Button } from 'components/Button';
import LoadingSkeleton from 'components/LoadingSkeleton';
import classNames from 'styles/utils/classNames';
import ContactInfoNotice from './ContactInfoNotice';
import DiscountCode from './DiscountCode';
import OrderItemList from './OrderItemList';
import { REGISTER_API, RegisterProps, RegistrationFormData } from './types';

interface Props extends RegisterProps {
  onSubmit: () => void | Promise<void>;
  orderTotal: OrderTotal;
  registrationFormData: RegistrationFormData;
  invitationId?: string;
  teamId?: string;
  appliedCoupon: EventCoupon | null;
  couponError?: string | null;
  onApplyCoupon?: (code: string) => void;
  onClearCoupon: () => void;
}

export default function Payment({
  onSubmit,
  event: tournament,
  orderTotal,
  registrationFormData,
  invitationId,
  teamId,
  user,
  appliedCoupon,
  couponError,
  onApplyCoupon,
  onClearCoupon,
}: Props) {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const [isStripeReady, setIsStripeReady] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [dialCode, setDialCode] = useState('');
  const { post: createTournamentPayment } = useApiGateway<PostRequestPayload>(REGISTER_API);
  const [updatePlayerPhoneMutation] = useUpdatePlayerPhoneMutation();
  const isSocialEvent = tournament?.type === EventTypesEnum.Social;

  useEffect(() => {
    if (!elements) {
      Sentry.captureException(
        new Error('Failed to initialize ELEMENTS for Stripe for tournament payment'),
      );
    }
  }, [elements]);

  useEffect(() => {
    if (!stripe) {
      Sentry.captureException(new Error('Failed to initialize STRIPE for tournament payment'));
    }
  }, [stripe]);

  const handlePhoneNumberChange = (phone: string, country: any): void => {
    const { dialCode } = country;

    setDialCode(`${toSafePhone(dialCode)}`);

    let inputPhoneNumber: string = toFormattedPhone(phone);
    setPhoneNumber(inputPhoneNumber);
  };

  return (
    <form
      onSubmit={async (event) => {
        event.preventDefault();

        if (!stripe || !elements) {
          return;
        }

        setIsLoading(true);

        try {
          const { setupIntent, error } = await stripe.confirmSetup({
            elements,
            redirect: 'if_required',
            confirmParams: { return_url: window.location.href },
          });

          if (error) {
            Sentry.captureException(error);
            alert(
              `There was an error signing up for the ${
                isSocialEvent ? 'social' : 'tournament'
              }. Refresh the page and try again.`,
            );
            setIsLoading(false);
            return;
          }

          const registrationResponse = await createTournamentPayment({
            payload: {
              providerCardId: setupIntent?.payment_method as string,
              isPayAtEvent: false,
              tournamentId: tournament?.id,
              duprId: registrationFormData.duprId,
              birthday: registrationFormData.birthday,
              groups: registrationFormData.groups,
              invitationId: invitationId,
              teamId: teamId,
              couponId: appliedCoupon?.id,
              referredBy: isSocialEvent
                ? registrationFormData.referredBy?.trim() || undefined
                : undefined,
              skillLevelId: isSocialEvent
                ? registrationFormData.skillLevelId || undefined
                : undefined,
            },
          });

          if (registrationResponse.isError) {
            toast.error(
              registrationResponse.error ||
                `There was an error registering for the ${
                  isSocialEvent ? 'social' : 'tournament'
                }.`,
            );
            setIsLoading(false);
            return;
          }

          const nationalNumber = stripDialCode(phoneNumber, dialCode);
          if (user?.id && nationalNumber && dialCode) {
            const playerVariables = {
              id: user.id,
              phoneNumber: nationalNumber,
              phoneCountryCode: toDigits(dialCode),
            };

            await updatePlayerPhoneMutation({
              variables: playerVariables,
            });
          }

          await onSubmit();

          setIsLoading(false);
        } catch (error) {
          Sentry.captureException(error);
          toast.error(
            `There was an error signing up for the ${
              isSocialEvent ? 'social' : 'tournament'
            }. Refresh the page and try again.`,
          );
          setIsLoading(false);
        } finally {
          setIsLoading(false);
        }
      }}
    >
      <div className="flex flex-auto flex-col px-6 pb-6">
        {onApplyCoupon && (
          <div className="mb-4">
            <DiscountCode
              appliedCoupon={appliedCoupon}
              onApply={onApplyCoupon}
              onClear={onClearCoupon}
              error={couponError}
            />
          </div>
        )}
        <OrderItemList orderTotal={orderTotal} appliedCoupon={appliedCoupon} />
        <div className="mt-4">
          {/* Skeleton shows until the Stripe PaymentElement reports ready via onReady. */}
          {!isStripeReady && (
            <div className="space-y-4">
              <div>
                <LoadingSkeleton count={1} />
                <LoadingSkeleton height="3rem" />
              </div>
              <div>
                <LoadingSkeleton count={1} />
                <LoadingSkeleton height="3rem" />
              </div>
              <div>
                <LoadingSkeleton count={1} />
                <LoadingSkeleton height="3rem" />
              </div>
            </div>
          )}
          {/* Keep the payment + phone block mounted (hidden) so PaymentElement can fire onReady. */}
          <div className={classNames(isStripeReady ? 'block' : 'hidden')}>
            <PaymentElement
              onReady={() => setIsStripeReady(true)}
              options={{
                defaultValues: {},
                fields: {
                  billingDetails: {
                    name: 'auto',
                  },
                },
              }}
            />

            <div className="mt-6">
              <label className="typography-product-subheading mb-ds-lg block">Phone Number</label>
              <PhoneInput
                inputProps={{
                  name: 'phone',
                  required: true,
                }}
                country={'us'}
                value={phoneNumber}
                onChange={handlePhoneNumberChange}
                inputClass={'!w-full !py-3'}
              />
              <ContactInfoNotice />
            </div>
          </div>
        </div>
      </div>
      <div className="sticky bottom-0 w-full px-6 pb-6">
        <Button
          loading={isLoading || !isStripeReady}
          disabled={!stripe || !elements || !isStripeReady || isLoading}
          type="submit"
          variant="brand"
          size="lg"
        >
          {isLoading ? 'Loading...' : !isStripeReady ? 'Loading payment…' : 'Register'}
        </Button>
      </div>
    </form>
  );
}
